import { SignedImage } from "@/lib/signedFile";
import {
  MapPin, Award, Send, X, Star, Briefcase, Building2, Calendar,
  Target, TrendingUp, GraduationCap, Sparkles, Video, Home, Globe2, Eye,
} from "lucide-react";

/* --------------------------------------------------------------------
 * Shared candidate presentation used by BOTH the recruiter's view
 * (`RecruiterHome`) and the platform admin's view
 * (`RecruiterNominationsAdmin`). Any change to the template here is
 * automatically reflected in both places — that's the "coupling"
 * requested by the platform owner.
 * -------------------------------------------------------------------- */

export const AVAILABILITY_STYLES = {
  onsite: { icon: Home,   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  remote: { icon: Video,  cls: "bg-sky-50 text-sky-700 border-sky-200" },
  hybrid: { icon: Globe2, cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

export function Stars({ value }) {
  const v = Number(value) || 0;
  const full = Math.floor(v);
  const half = v - full >= 0.5;
  return (
    <span className="inline-flex items-center gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < full || (i === full && half) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
          strokeWidth={1.5}
        />
      ))}
      <span className="text-xs font-bold text-slate-700 ms-1 arabic-numerals">{v.toFixed(1)}</span>
    </span>
  );
}

function CompetencyBlock({ icon: Icon, title, tint, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className={`inline-flex items-center gap-1.5 text-[11px] font-bold ${tint}`}>
        <Icon className="h-3.5 w-3.5" />
        <span>{title}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {items.map((t) => (
          <span key={t} className="px-2 py-0.5 rounded-full bg-white text-slate-700 text-[11px] border border-slate-200">{t}</span>
        ))}
      </div>
    </div>
  );
}

/**
 * Compact candidate card. Ships identical to recruiter & admin.
 * Optional `badge` slot allows the admin view to add moderation chips.
 */
export function CandidateCard({ student, opportunity, onOpen, onContact, ctaLabel = "تواصل", badge = null }) {
  const avail = AVAILABILITY_STYLES[student.availability] || AVAILABILITY_STYLES.hybrid;
  const AvailIcon = avail.icon;
  const overall = ((Number(student.assoc_rating || 0) + Number(student.mentor_rating || 0)) / 2).toFixed(1);
  const skills = student.competencies?.skills || [];
  return (
    <article
      className="group relative rounded-2xl bg-white border border-cream-200 card-shadow overflow-hidden flex flex-col cursor-pointer hover:-translate-y-0.5 hover:shadow-xl transition-all"
      data-testid={`grad-${student.id}`}
      onClick={() => onOpen && onOpen(student, opportunity)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (onOpen && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(student, opportunity); } }}
    >
      <div className="px-4 py-2.5 bg-gradient-to-l from-teal-700 to-teal-800 text-white text-xs">
        <div className="flex items-center gap-1.5 opacity-90">
          <Briefcase className="h-3 w-3" />
          <span className="font-bold">مرشّح لفرصة:</span>
        </div>
        <div className="mt-0.5 font-heading font-bold text-sm truncate">
          {opportunity?.title || "فرصة قيد التحديث"}
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-teal-700 to-teal-900 grid place-items-center text-white font-heading font-black text-base overflow-hidden shrink-0">
            {student.avatar_url
              ? <SignedImage fileId={student.avatar_url} alt="" className="h-full w-full object-cover" fallback={<span>{student.avatar_initial}</span>} />
              : student.avatar_initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-heading font-black text-slate-900 truncate leading-tight text-sm">{student.name}</div>
            <div className="text-[11px] text-slate-500 truncate mt-0.5">
              {[student.major, student.university].filter(Boolean).join(" · ")}
            </div>
          </div>
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-bold shrink-0 arabic-numerals">
            <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
            {overall}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 text-xs text-slate-700 min-w-0">
          <Building2 className="h-3.5 w-3.5 text-teal-700 shrink-0" />
          <span className="truncate"><span className="text-slate-500">من: </span><span className="font-bold">{student.association}</span></span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-slate-600">
          <MapPin className="h-3.5 w-3.5 text-teal-700 shrink-0" />
          <span className="truncate">{student.location}</span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${avail.cls}`}>
            <AvailIcon className="h-3 w-3" />
            {student.availability_label}
          </span>
          {skills.slice(0, 2).map((s) => (
            <span key={s} className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 text-[10px] font-semibold border border-teal-100">{s}</span>
          ))}
          {skills.length > 2 && (
            <span className="text-[10px] text-slate-500 font-semibold arabic-numerals">+{skills.length - 2}</span>
          )}
          {badge}
        </div>

        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1 text-teal-700 font-bold group-hover:gap-2 transition-all">
            <Eye className="h-3.5 w-3.5" /> عرض كامل البروفايل
          </span>
          {onContact && (
            <button
              onClick={(e) => { e.stopPropagation(); onContact(student); }}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-teal-700 text-white text-[11px] font-bold hover:bg-teal-600 transition-colors"
              data-testid={`contact-${student.id}`}
            >
              <Send className="h-3 w-3" /> {ctaLabel}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * Full-profile modal. `actionSlot` lets each caller inject a
 * bottom-CTA (recruiter → "طلب تواصل"، admin → إجراءات إشرافية).
 */
export function CandidateDetailModal({ payload, onClose, actionSlot = null }) {
  if (!payload) return null;
  const { student, opportunity } = payload;
  const avail = AVAILABILITY_STYLES[student.availability] || AVAILABILITY_STYLES.hybrid;
  const AvailIcon = avail.icon;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="student-detail-modal">
        <div className="sticky top-0 z-10 px-6 py-4 bg-gradient-to-l from-teal-700 to-teal-800 text-white flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs opacity-90">
              <Briefcase className="h-3.5 w-3.5" />
              <span className="font-bold">مرشّح لفرصة:</span>
            </div>
            <div className="mt-0.5 font-heading font-bold text-base truncate">
              {opportunity?.title || "فرصة قيد التحديث"}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 shrink-0" aria-label="إغلاق"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-start gap-3">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-teal-700 to-teal-900 grid place-items-center text-white font-heading font-black text-xl overflow-hidden shrink-0">
              {student.avatar_url
                ? <SignedImage fileId={student.avatar_url} alt="" className="h-full w-full object-cover" fallback={<span>{student.avatar_initial}</span>} />
                : student.avatar_initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading font-black text-slate-900 text-lg leading-tight">{student.name}</div>
              <div className="text-sm text-slate-500 mt-0.5">
                {[student.major, student.university].filter(Boolean).join(" · ")}
              </div>
              <div className={`mt-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-bold ${avail.cls}`}>
                <AvailIcon className="h-3.5 w-3.5" />
                {student.availability_label}
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-slate-50/70 border border-slate-100 p-4 space-y-2.5 text-sm">
            <div className="flex items-start gap-2">
              <Building2 className="h-4 w-4 text-teal-700 shrink-0 mt-0.5" />
              <div>
                <div className="text-xs text-slate-500">التدريب التعاوني في</div>
                <div className="font-bold text-slate-800">{student.association}</div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-teal-700 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500">الفترة</div>
                  <div className="font-semibold text-slate-800">{student.period}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-teal-700 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500">الموقع</div>
                  <div className="font-semibold text-slate-800">{student.location}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3">
              <div className="text-xs font-bold text-amber-800 mb-1.5">تقييم الجمعية</div>
              <Stars value={student.assoc_rating} />
            </div>
            <div className="rounded-xl border border-teal-100 bg-teal-50/40 p-3">
              <div className="text-xs font-bold text-teal-800 mb-1.5">تقييم الموجّه</div>
              <Stars value={student.mentor_rating} />
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2.5">أبرز الجدارات</div>
            <div className="space-y-3">
              <CompetencyBlock icon={GraduationCap} title="المعرفة"   tint="text-indigo-700" items={student.competencies?.knowledge} />
              <CompetencyBlock icon={Target}        title="المهارات"  tint="text-teal-700"   items={student.competencies?.skills} />
              <CompetencyBlock icon={Sparkles}      title="السلوكيات"  tint="text-rose-700"   items={student.competencies?.behaviors} />
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">أبرز المناصب الملائمة</div>
            <div className="flex flex-wrap gap-1.5">
              {(student.best_fit_roles || []).map((r) => (
                <span key={r} className="px-2.5 py-1 rounded-lg bg-teal-700/10 text-teal-800 text-xs font-semibold border border-teal-700/20">{r}</span>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> نقاط القوّة
            </div>
            <ul className="text-sm text-slate-700 space-y-1.5">
              {(student.key_strengths || []).map((k) => (
                <li key={k} className="flex items-start gap-2">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <span>{k}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {actionSlot && (
          <div className="sticky bottom-0 px-6 py-4 bg-white border-t border-slate-100 flex items-center gap-2">
            {actionSlot}
          </div>
        )}
      </div>
    </div>
  );
}
